/**
 * HSL Theme Presets
 *
 * Defines the 12 curated hue presets and appearance options
 * for the HSL-based theming system.
 */

// ============================================
// HUE PRESETS (12 curated colors)
// ============================================

export interface HuePreset {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Hue value (0-360) */
  hue: number;
  /** Emoji for visual reference */
  emoji: string;
}

/**
 * 12 curated hue presets distributed across the color wheel.
 * These are carefully chosen to look good with the HSL theming system.
 */
export const HUE_PRESETS: readonly HuePreset[] = [
  { id: 'red', name: 'Red', hue: 0, emoji: '🔴' },
  { id: 'orange', name: 'Orange', hue: 25, emoji: '🟠' },
  { id: 'amber', name: 'Amber', hue: 45, emoji: '🟡' },
  { id: 'yellow', name: 'Yellow', hue: 58, emoji: '🟡' },
  { id: 'lime', name: 'Lime', hue: 95, emoji: '🟢' },
  { id: 'green', name: 'Green', hue: 135, emoji: '🟢' },
  { id: 'teal', name: 'Teal', hue: 170, emoji: '🔵' },
  { id: 'cyan', name: 'Cyan', hue: 195, emoji: '🔵' },
  { id: 'blue', name: 'Blue', hue: 220, emoji: '🔵' },
  { id: 'indigo', name: 'Indigo', hue: 245, emoji: '🟣' },
  { id: 'violet', name: 'Violet', hue: 270, emoji: '🟣' },
  { id: 'pink', name: 'Pink', hue: 330, emoji: '🟣' },
] as const;

/**
 * Default hue (Amber) - matches CMG black & gold branding
 */
export const DEFAULT_HUE = 45;

/**
 * Get a preset by its hue value
 */
export function getPresetByHue(hue: number): HuePreset | undefined {
  return HUE_PRESETS.find((p) => p.hue === hue);
}

/**
 * Get a preset by its ID
 */
export function getPresetById(id: string): HuePreset | undefined {
  return HUE_PRESETS.find((p) => p.id === id);
}

/**
 * Find the nearest preset hue for any hue value
 */
export function getNearestPresetHue(hue: number): number {
  // Normalize hue to 0-360
  const normalizedHue = ((hue % 360) + 360) % 360;

  let nearestPreset = HUE_PRESETS[0];
  let minDistance = 360;

  for (const preset of HUE_PRESETS) {
    // Calculate circular distance
    const distance = Math.min(
      Math.abs(normalizedHue - preset.hue),
      360 - Math.abs(normalizedHue - preset.hue)
    );

    if (distance < minDistance) {
      minDistance = distance;
      nearestPreset = preset;
    }
  }

  return nearestPreset.hue;
}

// ============================================
// APPEARANCE PRESETS
// ============================================

export type AppearancePreset = 'balanced' | 'washed' | 'contrast';

export interface AppearanceConfig {
  /** Unique identifier */
  id: AppearancePreset;
  /** Display name */
  name: string;
  /** Description for UI */
  description: string;
  /** Brand saturation override */
  brandSaturation: number;
  /** Base saturation override (for tinted grays) */
  baseSaturation: number;
}

/**
 * Appearance presets control the overall saturation and contrast.
 */
export const APPEARANCE_PRESETS: readonly AppearanceConfig[] = [
  {
    id: 'balanced',
    name: 'Balanced',
    description: 'Default look with vibrant accents and subtle tints',
    brandSaturation: 92,
    baseSaturation: 12,
  },
  {
    id: 'washed',
    name: 'Washed',
    description: 'Muted, professional look with softer colors',
    brandSaturation: 62,
    baseSaturation: 6,
  },
  {
    id: 'contrast',
    name: 'Contrast',
    description: 'High-impact look with vivid colors',
    brandSaturation: 96,
    baseSaturation: 14,
  },
] as const;

/**
 * Default appearance preset
 */
export const DEFAULT_APPEARANCE: AppearancePreset = 'balanced';

/**
 * Get appearance config by ID
 */
export function getAppearanceConfig(
  id: AppearancePreset
): AppearanceConfig | undefined {
  return APPEARANCE_PRESETS.find((p) => p.id === id);
}

/**
 * Validate appearance preset string
 */
export function isValidAppearancePreset(
  value: string
): value is AppearancePreset {
  return ['balanced', 'washed', 'contrast'].includes(value);
}

// ============================================
// LIGHTNESS CURVES
// ============================================

/**
 * Brand color lightness values by shade (for accent colors)
 * Used in: hsl(--brand-h, --brand-s, L%)
 */
export const BRAND_LIGHTNESS_CURVE = {
  50: 97,
  100: 94,
  200: 86,
  300: 78,
  400: 66,
  500: 50, // Primary brand color
  600: 42,
  700: 34,
  800: 26,
  900: 16,
  950: 10,
} as const;

/**
 * Slate lightness values (for light mode tinted grays)
 * Used in: hsl(--base-h, --base-s, L%)
 */
export const SLATE_LIGHTNESS_CURVE = {
  50: 97, // App background
  100: 94, // Card background
  200: 86, // Borders
  300: 78, // Heavy borders
  400: 70,
  500: 62,
  600: 54,
  700: 38,
  800: 24,
  900: 10, // Primary text
  950: 6,
} as const;

/**
 * Zinc saturation + lightness values (for dark mode)
 * Custom saturation curve for rich dark backgrounds
 * Used in: hsl(--base-h, S%, L%)
 */
export const ZINC_CURVE = {
  950: { saturation: 25, lightness: 5 }, // App background (richest)
  900: { saturation: 20, lightness: 10 }, // Card background
  800: { saturation: 15, lightness: 15 }, // Hovers
  700: { saturation: 12, lightness: 20 }, // Borders
  600: { saturation: 10, lightness: 30 },
  500: { saturation: 10, lightness: 40 },
  400: { saturation: 10, lightness: 55 },
  300: { saturation: 10, lightness: 70 },
  200: { saturation: 10, lightness: 82 },
  100: { saturation: 12, lightness: 90 },
  50: { saturation: 15, lightness: 96 }, // Primary text (lightest)
} as const;
